import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { History } from 'history';

import { CardElement } from '@stripe/react-stripe-js';
import { Stripe, PaymentMethod, StripeError, StripeElements, CreatePaymentMethodCardData, StripeCardElement } from '@stripe/stripe-js';

import { receiveError, receiveSuccess } from '../../../store/actions/notificationAction';
import { receiveAwbs } from '../../../store/actions/awbsAction';
import { updateWholeCart } from '../../../store/actions/cartAction';
import { getGhaInfo } from '../../../store/actions/ghaAction';
import { fetchFinish, fetchPending } from '../../../store/actions/fetchingAction';

import cartRemote from '../../../api/cartRemote';
import paymentsRemote from '../../../api/paymentsRemote';
import paymentConnectRemote from '../../../api/paymentConnectRemote';

import PaymentPage from './PaymentPage';
import ConfirmationDialog from '../../confirmation/ConfirmationDialog';

import Cart from '../../../models/Cart';
import Payment from '../../../models/Payment';
import GhaModel from '../../../models/Gha';
import CartItem from '../../../models/CartItem';
import ApiResponse from '../../../models/ApiResponse';
import User, { UserRoleType } from '../../../models/User';
import { PaymentType } from '../../../models/PaymentType.enum';
import ResponseError from '../../../models/ResponseError';
import ManualEntryEdit from '../../../models/ManualEntryEdit';
import CartCalculations from '../../../models/CartCalculation';
import GhaFwdConfigModel from '../../../models/GhaFwdConfig';
import { InvoiceAccountType } from '../../../models/ForwarderAccountAndConfig';
import ForwarderInvoiceBalance from '../../../models/ForwarderGhaInvoiceBalance';

import { CART_CALCULATION } from '../../../utils/cartCalculation';
import { formatCurrency } from '../../../utils/currencyFormatter';
import { reMapAccountBalanceNames } from '../../../utils/accountBalance';

import paymentPageStyles from './paymentPage.module.css';
import AlternativePaymentOptions from '../../../models/PaymentOptions';
import { setWaitListId } from '../../../store/actions/waitListAction';
import { receiveBalance } from '../../../store/actions/invoiceFwdAction';

interface PaymentPageContainerProps {
  user: User;
  cart: any;
  port: string;
  ghaInfo: GhaModel;
  receiveError: Function;
  receiveSuccess: Function;
  updateCart: Function;
  history: History;
  onReceiveAwbs: Function;
  userRole: UserRoleType;
  getGhaInfo: Function;
  fwdInvoiceBalance: ForwarderInvoiceBalance;
  fetchFinish: Function;
  fetchPending: Function;
  manualEntryEdit: ManualEntryEdit;
  setWaitListId: (driverId: number) => void;
  receiveFwdBalance: (ghaId: number) => void;
  closeModal: Function;
  orderNumber?: string;
  overrideAmountDue?: number;
  overrideCartItemsLength?: number;
  alternativePaymentOnly?: boolean;
  customHandlePayment?: (paymentOther: string, paymentType: string, stripePaymentMethodId?: string) => void;
};

interface PaymentPageContainerState {
  subscribed: boolean;
  isConfirmationOpen: boolean;
  noPoConfirmationOpen: boolean;
  noPoPaySavedConfirmationOpen: boolean;
  expandPrevPayments: boolean;
  payments: Array<Payment>;
  paymentInfo: string;
  paymentType: string;
  ghaId: number;
  ghaFwdConfig: GhaFwdConfigModel;
  verifiedAchPayment: boolean;
  cartCalculations: CartCalculations;
  errorMessageCardElement: string;
  disableStripeBtn: boolean;
  achChargeConfirmationOpen: boolean;
  generatePO: boolean;
  redirectToOrderManualEntryEdit: boolean;
  disableButton: boolean;
  paymentInfoSelect: string;
  paymentOptions: AlternativePaymentOptions;
};

class PaymentPageContainer extends Component <PaymentPageContainerProps, PaymentPageContainerState> {

  state: PaymentPageContainerState = {
    subscribed: false,
    isConfirmationOpen: false,
    noPoConfirmationOpen: false,
    noPoPaySavedConfirmationOpen: false,
    paymentInfoSelect: '',
    expandPrevPayments: false,
    payments: [],
    paymentInfo: '',
    paymentType: '',
    ghaId: 0,
    ghaFwdConfig: { convenience_fee_usd: 0, pay_by_invoice_allowed: false, type: InvoiceAccountType.NONE, other_payment_allowed: 'no' as const, pay_by_wallet: false},
    verifiedAchPayment: false,
    cartCalculations: CART_CALCULATION,
    errorMessageCardElement: '',
    disableStripeBtn: false,
    achChargeConfirmationOpen: false,
    generatePO: false,
    redirectToOrderManualEntryEdit: false,
    disableButton: false,
    paymentOptions: {} as AlternativePaymentOptions
  };

  componentDidMount() {

    if (this.props.overrideAmountDue !== undefined) {
      // Split GO->PO flow can legitimately have $0 due; still allow final submission.
      this.setState({ subscribed: true });
      this.getAltPaymentOptions();
      if (this.props.userRole === UserRoleType.FORWARDER) {
        this.getAchPaymentAllowed();
      }
      return;
    }

    if (this.props.userRole === UserRoleType.FORWARDER || (this.props.ghaInfo.gha_fwd_config && this.props.ghaInfo.gha_fwd_config.convenience_fee_usd)) {
      this.getCartCalculations();
    }
    if (this.props.ghaInfo && this.props.ghaInfo.id !== -1 && this.props.ghaInfo.gha_fwd_config.pay_by_invoice_allowed) {
      this.props.receiveFwdBalance(this.props.ghaInfo.id);
    };
    this.getGhaCart();
    this.getAltPaymentOptions();
    if (this.props.userRole === UserRoleType.FORWARDER) {
      this.getAchPaymentAllowed();
    }
  }

  componentDidUpdate(prevProps: PaymentPageContainerProps) {

    if (this.props.overrideAmountDue !== undefined) return;

    if (prevProps.cart !== this.props.cart) {
      this.getPreviousPayments(this.props.cart.items);

      if (this.props.userRole === UserRoleType.FORWARDER || (this.props.ghaInfo.gha_fwd_config)) {
        this.getCartCalculations();
      }
    }
    const newProps: PaymentPageContainerProps = this.props;
    if (prevProps.ghaInfo !== newProps.ghaInfo && newProps.ghaInfo && this.props.ghaInfo.id !== -1 && this.props.ghaInfo.gha_fwd_config.pay_by_invoice_allowed) {
      this.props.receiveFwdBalance(this.props.ghaInfo.id);
    };
  }

  getGhaCart = (): void => {

    cartRemote.getCartGha().then((resp: ApiResponse<GhaModel>) => {

      const gha: GhaModel | undefined = resp.result;
      const error: ResponseError | undefined = resp.error;

      if (gha) {
        this.props.getGhaInfo(gha.id);
        this.setState({ subscribed: gha.info.po_support, ghaId: gha.id, ghaFwdConfig: gha.gha_fwd_config });
      }
    });
  }

  getCartCalculations = (): void => {

    cartRemote.getCartPay().then((resp: ApiResponse<CartCalculations>) => {

      const cartCalculations: CartCalculations | undefined = resp.result;
      const error: ResponseError | undefined = resp.error;
      
      if (cartCalculations) {
        this.setState({ cartCalculations })
      }

      if (error) {
        this.props.receiveError(error);
      }
    });
  }

  getAltPaymentOptions = (): void => {

    paymentsRemote.getPaymentOptions().then((resp: ApiResponse<AlternativePaymentOptions>) => {

      const paymentOptions: AlternativePaymentOptions | undefined = resp.result;
      const error: ResponseError | undefined = resp.error;
      
      if (paymentOptions) {
        this.setState({ paymentOptions })
      }

      if (error) {
        this.props.receiveError(error);
      }
    });
  }

  getAchPaymentAllowed = (): void => {
    
    if (!this.props.user.forwarder_id) {
      return;
    }

    paymentConnectRemote.getConnectedAch(this.props.user.forwarder_id).then((resp: ApiResponse<boolean>) => {

      const success: boolean | undefined = resp.success;
      const error: ResponseError | undefined = resp.error;

      if (success) {
        this.setState({ verifiedAchPayment: success });
      }
  
      if (error) {
        this.props.receiveError(error);
      }
    });
  }

  getPreviousPayments = (items: Array<CartItem>): void => {
    
    const awbs: Array<{ code: string; number: string; }> = [];

    for (const awb of items) {
      awbs.push({ code: awb.code, number: awb.number });
    }

    paymentsRemote.getPaymentsAwbFwd(awbs, '').then((resp: ApiResponse<Array<Payment>>) => {

      const payments: Array<Payment> | undefined = resp.result;
      const error: ResponseError | undefined = resp.error;

      if (payments) {
        this.setState({ payments });
      }

      if (error) {
        this.props.receiveError(error);
      }
    });
  }

  preHandlePayment = (generatePO: boolean, stripePaymentMethodId?: string, redirectToOrderManualEntryEdit?: boolean): void => {

    if (this.state.paymentType === PaymentType.ACH) {

      return this.setState({ achChargeConfirmationOpen: true, generatePO });
    }

    this.setState({
        generatePO,
        disableButton: true,
        redirectToOrderManualEntryEdit: redirectToOrderManualEntryEdit ? true : false
      },
      () => this.handelPayment(stripePaymentMethodId)
    );
  }

  handelPayment = (stripePaymentMethodId?: string): void => {

    const generatePO: boolean = this.state.generatePO;
    this.setState({ achChargeConfirmationOpen: false });
    let paymentOther = this.state.paymentInfoSelect === 'other' ? this.state.paymentInfo : this.state.paymentInfoSelect;

    if (this.state.paymentType === PaymentType.ACH) {
      this.props.fetchPending();
    }

    if (this.props.customHandlePayment) {
      this.props.customHandlePayment(paymentOther, this.state.paymentType, stripePaymentMethodId);
      return;
    }

    cartRemote.cartPay(false, paymentOther, this.state.paymentType, stripePaymentMethodId, this.props.orderNumber).then((resp: ApiResponse<Cart>) => {

      const cart: Cart | undefined = resp.result;
      const error: ResponseError | undefined = resp.error;

      if (cart) {
        this.props.closeModal('cart');
        this.props.updateCart(cart);
      }

      if (error) {
        this.setState({ noPoConfirmationOpen: false, achChargeConfirmationOpen: false });
        this.props.receiveError(error);
      }

      if (stripePaymentMethodId || this.state.paymentType === PaymentType.ACH) {
        this.props.fetchFinish();
      }
    }); 
  }

  openNoPoPayConfirmation = (): void => {
    this.setState({ noPoConfirmationOpen: true, generatePO: false });
  }

  handleConfirmAfterPay = (): void => {

    if (this.props.userRole === UserRoleType.FORWARDER) {

      if (this.state.subscribed) {
        this.props.history.push('/issue-po');
        
      } else {
        this.props.history.push('/');
      }

    } else {
      this.props.history.push('/manual-entry-issue-po');   
    }
  }

  handleChange = (event: any, propertyName?: string): void => {

    switch (propertyName) {

      case 'expand': this.setState({ expandPrevPayments: !this.state.expandPrevPayments }); break;
      case 'paymentInfo': this.setState({ paymentInfo: event.target.value }); break;
      case 'paymentInfoSelect': 
        this.setState({ paymentInfoSelect: event.target.value });
        this.handleClickTextField(event); 
        break;
      default: break;
    }
  }

  handleChangePaymentType = (event: any): void => {

    const paymentType: string = event.target.value;
    this.setState({ paymentType,  paymentInfo: '', errorMessageCardElement: '' });
  }

  handleClickTextField = (event: any): void => {

    this.setState({ paymentType : PaymentType.ALTERNATIVE_PAYMENT });
  }

  handleStripePayment = (stripe: Stripe, elements: StripeElements, generatePO: boolean ): void => {

    if (!stripe || !elements) {
      return;
    }

    this.setState({ disableStripeBtn: true, generatePO });

    const card: StripeCardElement | null = elements.getElement(CardElement);

    if (card) {

      const createPaymentData: CreatePaymentMethodCardData = {
        type: 'card',
        card,
        billing_details: {}
      };
    
      stripe.createPaymentMethod(createPaymentData).then((resp: { paymentMethod?: PaymentMethod; error?: StripeError }) => {

        const paymentMethod: PaymentMethod | undefined = resp.paymentMethod;
        const error: StripeError | undefined = resp.error;

        if (paymentMethod) {
          this.setState({ disableStripeBtn: false });
          this.props.fetchPending();
          this.handelPayment(paymentMethod.id);
        }

        if (error) {
          this.setState({ errorMessageCardElement: error.message? error.message : '', disableStripeBtn: false });
        }
      });
    }
  }

  onChangeCardElement = (event: any) => {

    const error = event.error;

    if (!error) {
      this.setState({ errorMessageCardElement: ''});
    }
  }

  manualEntryProcessRedirectToPickupOrder = (): void => {

    this.props.history.push('/manual-entry-issue-po');
  }

  openNoPOPayConfirmationME = (): void => {
    this.setState({ noPoConfirmationOpen: true, generatePO: false });
  }

  render() {
    
    const remainDue: number = this.props.overrideAmountDue !== undefined ? this.props.overrideAmountDue : this.props.cart.remainDue;
    const cartItemsLength: number = this.props.overrideCartItemsLength !== undefined ? this.props.overrideCartItemsLength : this.props.cart.items.length;
    const disabledPay: boolean = remainDue <= 0 && (this.props.cart && !this.props.cart.comat) ? true : false;
    const isGeneratePickUpOrder: boolean = this.state.subscribed && remainDue <= 0;
    const textConfirm: string = this.state.subscribed ? 'Proceed to Pick Up Order' : 'Return to AWBs List';
    let renderPay: JSX.Element = <div className={ paymentPageStyles.messageEmpty }>Your Cart is empty. To make a payment, you need to add AWB to Cart first!</div>;
    let noPoConfirmationText: string = 'Payment will be registered for all the selected AWBs. Your Cart will be emptied.';
    let noPoConfirmationSavedText: string = 'Return to Orders List';
    let noPoConfirmationSavedUrl: string = '/manage-imports';

    if (!this.props.manualEntryEdit.isEditMode) {
      noPoConfirmationText += ' Do you wish to proceed without generating Pick Up Order?';
      noPoConfirmationSavedText = 'Return to List of AWBs';
      noPoConfirmationSavedUrl = '/';
    }

    if (cartItemsLength > 0) {
      renderPay = 
        <PaymentPage 
          disabledPay={ disabledPay }
          due={ remainDue }
          handlePay={ this.preHandlePayment }
          openNoPoPayConfirmation={ this.openNoPoPayConfirmation }
          isGeneratePickUpOrder={ isGeneratePickUpOrder }
          expandPrevPayments={ this.state.expandPrevPayments }
          handleChange={ this.handleChange }
          payments={ this.state.payments }
          paymentInfo={ this.state.paymentInfo }
          paymentType={ this.state.paymentType }
          handleChangePaymentType={ this.handleChangePaymentType }
          handleClickTextField={ this.handleClickTextField }
          ghaFwdConfig={ this.props.ghaInfo.gha_fwd_config }
          userRole={ this.props.userRole }
          fwdInvoiceBalance={ this.props.fwdInvoiceBalance }
          verifiedAchPayment={ this.state.verifiedAchPayment }
          cartCalculations={ this.state.cartCalculations }
          handleStripePayment={ this.handleStripePayment }
          errorMessageCardElement={ this.state.errorMessageCardElement }
          onChangeCardElement={ this.onChangeCardElement }
          disableStripeBtn={ this.state.disableStripeBtn }
          redirectToPickupOrder={ this.manualEntryProcessRedirectToPickupOrder }
          disableButton={ this.state.disableButton }
          paymentInfoSelect={ this.state.paymentInfoSelect }
          paymentOptions={ this.state.paymentOptions }
          comat={ this.props.cart ? this.props.cart.comat : false }
          alternativePaymentOnly={ this.props.alternativePaymentOnly }
        />
    }
   
    return (
      <Fragment>
        { renderPay }
        { this.state.isConfirmationOpen &&
          <ConfirmationDialog 
            open={ this.state.isConfirmationOpen }
            title='Payment confirmation'
            text='Your payment has been submitted for processing'
            close={ () => { this.setState({ isConfirmationOpen: false }) }}
            confirm={ this.handleConfirmAfterPay }
            textConfirm={ textConfirm }
            showCloseIcon={ false }
          />
        }
        { this.state.noPoConfirmationOpen &&
          <ConfirmationDialog 
            open={ this.state.noPoConfirmationOpen }
            title='Payment confirmation'
            text={ noPoConfirmationText }
            close={ () => { this.setState({ noPoConfirmationOpen: false }) } }
            confirm={ () => {
              this.setState({ noPoConfirmationOpen: false });
              this.preHandlePayment(false) }
            }
            textConfirm={ 'OK' }
            showCloseIcon={ true }
          />
        }
        { this.state.noPoPaySavedConfirmationOpen &&
          <ConfirmationDialog 
            open={ this.state.noPoPaySavedConfirmationOpen }
            title='Payment information'
            text='Payment information saved.'
            close={ () => { this.setState({ noPoPaySavedConfirmationOpen: false }) } }
            confirm={ () => { this.props.history.push(noPoConfirmationSavedUrl) } }
            textConfirm={ noPoConfirmationSavedText }
            showCloseIcon={ true }
          />
        }
        { this.state.achChargeConfirmationOpen && this.state.cartCalculations.ach &&
          <ConfirmationDialog
            open={ this.state.achChargeConfirmationOpen }
            close={ () => { this.setState({ achChargeConfirmationOpen: false }) } }
            title='An ACH charge confirmation'
            text={ `Your bank account will be charged with ${ formatCurrency(this.state.cartCalculations.ach.achTotal, 2, '$') }. Do you want to proceed with payment?` }
            textConfirm='Proceed'
            confirm={ () => this.handelPayment() }
            showCloseIcon={ false }
            showCancelButton={ true }
          />     
        }
      </Fragment>
    );
  };
};

const mapStateToProps = (state: any) => {
  return {
    user: state.user,
    cart: state.cart,
    port: state.port,
    ghaInfo: state.ghaInfo,
    fwdInvoiceBalance: reMapAccountBalanceNames(state.fwdBalance),
    manualEntryEdit: state.manualEntryEdit
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    onReceiveAwbs: (port: string, gha: number) => dispatch(receiveAwbs(port, gha)),
    receiveError: (error: ResponseError) => dispatch(receiveError(error)),
    receiveSuccess: (message: string) => dispatch(receiveSuccess(message)),
    updateCart: (cart: Cart) => dispatch(updateWholeCart(cart)),
    getGhaInfo: (ghaId: number) => dispatch(getGhaInfo(ghaId)),
    fetchFinish: () => dispatch(fetchFinish()),
    fetchPending: () => dispatch(fetchPending()),
    setWaitListId: (driverId: number) => dispatch(setWaitListId(driverId)),
    receiveFwdBalance: (ghaId: number) => dispatch(receiveBalance(ghaId))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PaymentPageContainer);
